import {
  GET_SHIFT_SUMMARY_SUCCESS,
  GET_SHIFT_SUMMARY_FAIL,
  SET_GET_SHIFT_SUMMARY_LOADING,
  GET_DAILY_SUMMARY_SUCCESS,
  GET_DAILY_SUMMARY_FAIL,
  SET_GET_DAILY_SUMMARY_LOADING,
  GET_DAILY_CHART_SUCCESS,
  GET_DAILY_CHART_FAIL,
  SET_GET_DAILY_CHART_LOADING,
  GET_PAYMENT_LEDGER_SUCCESS,
  GET_PAYMENT_LEDGER_FAIL,
  SET_GET_PAYMENT_LEDGER_LOADING,
  GET_PAYMENT_LEDGER_METHODS_SUCCESS,
  GET_PAYMENT_LEDGER_METHODS_FAIL,
  SET_GET_PAYMENT_LEDGER_METHODS_LOADING,
  GET_BUSINESS_DAY_PARTICIPATION_SUCCESS,
  GET_BUSINESS_DAY_PARTICIPATION_FAIL,
  SET_GET_BUSINESS_DAY_PARTICIPATION_LOADING,
} from './types';
import store from '../../../../store';
import Axios from '../../../../common/util/Axios';

const appendShiftFilters = (queryParams, filters = {}) => {
  if (filters.cashierRegistryId) {
    queryParams.cashierRegistryId = filters.cashierRegistryId;
  }
  if (filters.userId) {
    queryParams.userId = filters.userId;
  }
  if (filters.status) {
    queryParams.status = filters.status;
  }
};

const appendDailyFilters = (queryParams, filters = {}) => {
  if (filters.cashierRegistryId) {
    queryParams.cashierRegistryId = filters.cashierRegistryId;
  }
  if (filters.status) {
    queryParams.status = filters.status;
  }
};

const buildBaseQueryParams = (filters = {}) => {
  const companyId = store.getState().authState.companyId;
  const branchId = store.getState().authState.branchId;

  const queryParams = {
    fromDate: filters.fromDate || '',
    toDate: filters.toDate || '',
    page: filters.page || 1,
    limit: filters.limit || 10,
    companyId: companyId || '',
    branchId: branchId || '',
  };
  if (filters.periodType === 'businessDay') {
    queryParams.periodType = 'businessDay';
    queryParams.startBusinessDayId = filters.startBusinessDayId;
    queryParams.endBusinessDayId = filters.endBusinessDayId;
  }
  return queryParams;
};

/**
 * Get cash balance shift summary with filters and pagination
 */
export const getShiftSummary = (filters = {}) => async (dispatch) => {
  try {
    dispatch({
      type: SET_GET_SHIFT_SUMMARY_LOADING,
      payload: true,
    });

    const queryParams = buildBaseQueryParams(filters);
    appendShiftFilters(queryParams, filters);

    const res = await Axios.get('/api/dashboard/reports/cash-balance/shift-summary', {
      params: queryParams,
    });

    if (res.data && res.data.success) {
      dispatch({
        type: GET_SHIFT_SUMMARY_SUCCESS,
        payload: {
          data: res.data.data,
          pagination: res.data.pagination,
        },
      });
    } else {
      dispatch({
        type: GET_SHIFT_SUMMARY_FAIL,
        payload: res.data?.error || 'Failed to fetch shift summary',
      });
    }
  } catch (error) {
    console.error('Error fetching shift summary:', error);
    dispatch({
      type: GET_SHIFT_SUMMARY_FAIL,
      payload:
        error.response?.data?.error ||
        error.message ||
        'Error fetching shift summary',
    });
  } finally {
    dispatch({
      type: SET_GET_SHIFT_SUMMARY_LOADING,
      payload: false,
    });
  }
};

/**
 * Get cash balance daily summary with filters and pagination
 */
export const getDailySummary = (filters = {}) => async (dispatch) => {
  try {
    dispatch({
      type: SET_GET_DAILY_SUMMARY_LOADING,
      payload: true,
    });

    const queryParams = buildBaseQueryParams(filters);
    appendDailyFilters(queryParams, filters);

    const res = await Axios.get('/api/dashboard/reports/cash-balance/daily-summary', {
      params: queryParams,
    });

    if (res.data && res.data.success) {
      dispatch({
        type: GET_DAILY_SUMMARY_SUCCESS,
        payload: {
          data: res.data.data,
          pagination: res.data.pagination,
        },
      });
    } else {
      dispatch({
        type: GET_DAILY_SUMMARY_FAIL,
        payload: res.data?.error || 'Failed to fetch daily summary',
      });
    }
  } catch (error) {
    console.error('Error fetching daily summary:', error);
    dispatch({
      type: GET_DAILY_SUMMARY_FAIL,
      payload:
        error.response?.data?.error ||
        error.message ||
        'Error fetching daily summary',
    });
  } finally {
    dispatch({
      type: SET_GET_DAILY_SUMMARY_LOADING,
      payload: false,
    });
  }
};

/**
 * Get cash balance daily end-variance chart (one series per cashier).
 */
export const getDailyChart = (filters = {}) => async (dispatch) => {
  try {
    dispatch({
      type: SET_GET_DAILY_CHART_LOADING,
      payload: true,
    });

    const queryParams = buildBaseQueryParams(filters);
    delete queryParams.page;
    delete queryParams.limit;
    appendDailyFilters(queryParams, filters);

    const res = await Axios.get('/api/dashboard/reports/cash-balance/daily-chart', {
      params: queryParams,
    });

    if (res.data && res.data.success) {
      dispatch({
        type: GET_DAILY_CHART_SUCCESS,
        payload: res.data.data || { dates: [], series: [] },
      });
    } else {
      dispatch({
        type: GET_DAILY_CHART_FAIL,
        payload: res.data?.error || 'Failed to fetch daily chart',
      });
    }
  } catch (error) {
    console.error('Error fetching daily chart:', error);
    dispatch({
      type: GET_DAILY_CHART_FAIL,
      payload:
        error.response?.data?.error ||
        error.message ||
        'Error fetching daily chart',
    });
  } finally {
    dispatch({
      type: SET_GET_DAILY_CHART_LOADING,
      payload: false,
    });
  }
};

const appendPaymentLedgerFilters = (queryParams, filters = {}) => {
  if (filters.companyPaymentMethodId) {
    queryParams.companyPaymentMethodId = filters.companyPaymentMethodId;
  }
};

/**
 * Payment methods that have ledger rows in the selected date range.
 */
export const getPaymentLedgerMethods = (filters = {}) => async (dispatch) => {
  try {
    dispatch({
      type: SET_GET_PAYMENT_LEDGER_METHODS_LOADING,
      payload: true,
    });

    const queryParams = buildBaseQueryParams(filters);
    delete queryParams.page;
    delete queryParams.limit;

    const res = await Axios.get(
      '/api/dashboard/reports/cash-balance/payment-ledger/methods',
      { params: queryParams }
    );

    if (res.data && res.data.success) {
      dispatch({
        type: GET_PAYMENT_LEDGER_METHODS_SUCCESS,
        payload: {
          data: res.data.data || [],
          defaultCompanyPaymentMethodId:
            res.data.defaultCompanyPaymentMethodId || null,
        },
      });
    } else {
      dispatch({
        type: GET_PAYMENT_LEDGER_METHODS_FAIL,
        payload: res.data?.error || 'Failed to fetch payment methods',
      });
    }
  } catch (error) {
    console.error('Error fetching payment ledger methods:', error);
    dispatch({
      type: GET_PAYMENT_LEDGER_METHODS_FAIL,
      payload:
        error.response?.data?.error ||
        error.message ||
        'Error fetching payment methods',
    });
  } finally {
    dispatch({
      type: SET_GET_PAYMENT_LEDGER_METHODS_LOADING,
      payload: false,
    });
  }
};

/**
 * Get paginated payment ledger for a payment method and date range.
 */
export const getPaymentLedger = (filters = {}) => async (dispatch) => {
  try {
    dispatch({
      type: SET_GET_PAYMENT_LEDGER_LOADING,
      payload: true,
    });

    const queryParams = buildBaseQueryParams(filters);
    appendPaymentLedgerFilters(queryParams, filters);

    const res = await Axios.get(
      '/api/dashboard/reports/cash-balance/payment-ledger',
      { params: queryParams }
    );

    if (res.data && res.data.success) {
      dispatch({
        type: GET_PAYMENT_LEDGER_SUCCESS,
        payload: {
          data: res.data.data,
          pagination: res.data.pagination,
        },
      });
    } else {
      dispatch({
        type: GET_PAYMENT_LEDGER_FAIL,
        payload: res.data?.error || 'Failed to fetch payment ledger',
      });
    }
  } catch (error) {
    console.error('Error fetching payment ledger:', error);
    dispatch({
      type: GET_PAYMENT_LEDGER_FAIL,
      payload:
        error.response?.data?.error ||
        error.message ||
        'Error fetching payment ledger',
    });
  } finally {
    dispatch({
      type: SET_GET_PAYMENT_LEDGER_LOADING,
      payload: false,
    });
  }
};

export const getBusinessDayParticipation = (filters = {}) => async (dispatch) => {
  try {
    dispatch({
      type: SET_GET_BUSINESS_DAY_PARTICIPATION_LOADING,
      payload: true,
    });

    const queryParams = buildBaseQueryParams(filters);
    if (filters.cashierRegistryId) {
      queryParams.cashierRegistryId = filters.cashierRegistryId;
    }

    const res = await Axios.get('/api/dashboard/reports/cash-balance/business-day', {
      params: queryParams,
    });

    if (res.data && res.data.success) {
      dispatch({
        type: GET_BUSINESS_DAY_PARTICIPATION_SUCCESS,
        payload: {
          data: res.data.data,
          pagination: res.data.pagination,
        },
      });
    } else {
      dispatch({
        type: GET_BUSINESS_DAY_PARTICIPATION_FAIL,
        payload: res.data?.error || 'Failed to fetch business day participation',
      });
    }
  } catch (error) {
    dispatch({
      type: GET_BUSINESS_DAY_PARTICIPATION_FAIL,
      payload:
        error.response?.data?.error ||
        error.message ||
        'Error fetching business day participation',
    });
  } finally {
    dispatch({
      type: SET_GET_BUSINESS_DAY_PARTICIPATION_LOADING,
      payload: false,
    });
  }
};
